import { useState, type FormEvent } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { api, type ApiError } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'

// DIS-12: first-run setup. Reachable only while no user exists (root guard).
export const Route = createFileRoute('/setup')({
  component: SetupPage,
})

function SetupPage() {
  const { auth } = Route.useRouteContext()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFields({})
    const form = new FormData(e.currentTarget)
    const { data, error: err, response } = await api.POST('/setup', {
      body: {
        workspace_name: String(form.get('workspace_name') ?? ''),
        email: String(form.get('email') ?? ''),
        display_name: String(form.get('display_name') ?? ''),
        password: String(form.get('password') ?? ''),
      },
    })
    setBusy(false)
    if (data) {
      await auth.invalidate()
      await navigate({ to: '/' })
      return
    }
    const apiErr = err as ApiError | undefined
    if (apiErr?.fields) setFields(apiErr.fields)
    setError(apiErr?.message ?? `Setup failed (${response.status})`)
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-md space-y-5 rounded-lg border border-border bg-bg-muted p-6">
        <div>
          <h1 className="text-lg font-semibold">Set up Dispatch</h1>
          <p className="text-sm text-fg-muted">Name your workspace and create the first admin account.</p>
        </div>

        <Field label="Workspace name" name="workspace_name" error={fields.workspace_name}>
          <Input name="workspace_name" required maxLength={80} autoFocus placeholder="Bitcairns" />
        </Field>
        <Field label="Your name" name="display_name" error={fields.display_name}>
          <Input name="display_name" required maxLength={80} autoComplete="name" />
        </Field>
        <Field label="Email" name="email" error={fields.email}>
          <Input name="email" type="email" required autoComplete="username" />
        </Field>
        <Field label="Password" name="password" error={fields.password} hint="At least 12 characters.">
          <Input name="password" type="password" required minLength={12} autoComplete="new-password" />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Creating…' : 'Create workspace'}
        </Button>
      </form>
    </div>
  )
}

function Field({
  label,
  name,
  error,
  hint,
  children,
}: {
  label: string
  name: string
  error?: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block space-y-1.5" htmlFor={name}>
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-fg-muted">{hint}</span>
      ) : null}
    </label>
  )
}
